import React, { useContext, useEffect, useRef, useState } from "react";
import { Prompt, __RouterContext as RouterContext } from "react-router";
import Button from "./Button/Button.js";
import { isAutoSaveActive } from "../hooks/useAutoSave.js";
import { FORM_DIRTY_EVENT } from "../Util/formDirty.js";

const UNSAVED_MESSAGE = "Leave this page? Changes you haven't saved will be lost.";

/**
 * Warns before leaving a form the user has typed into but not saved — route
 * changes (Back, menu, palette) and tab close/reload. Watches the enclosing
 * <form>: the first input/change marks it dirty, a submit marks it clean (so a
 * successful save that navigates away never prompts). Skipped while the page
 * autosaves a draft (useAutoSave): leaving loses nothing there. Fields inside
 * [data-no-dirty] (e.g. scan boxes, filters) don't count as edits.
 */
// Snapshot of what the form shows (controlled inputs keep the DOM value current).
const formSnapshot = (form) =>
    [...form.querySelectorAll("input, textarea, select")]
        .filter((el) => !el.closest("[data-no-dirty]") && el.type !== "hidden")
        .map((el) => (el.type === "checkbox" || el.type === "radio" ? String(el.checked) : el.value))
        .join("\u0001");

function UnsavedGuard({ disabled, submitTick = 0 }) {
    const anchor = useRef(null);
    const [dirty, setDirty] = useState(false);
    const dirtyRef = useRef(false);
    dirtyRef.current = dirty;
    const inRouter = !!useContext(RouterContext);
    // Set at submit: was there unsaved work, and what did the form show then?
    const pending = useRef(null);

    // Submitting marks the form clean so a successful save that navigates away
    // never prompts. If the save FAILS the page stays with the same values —
    // re-arm then (it used to stay disarmed, so leaving after a 400 lost edits).
    // A page that saves and stays open resets its fields, so it doesn't re-arm.
    const startSubmit = (form) => {
        pending.current = { wasDirty: dirtyRef.current, snapshot: formSnapshot(form) };
        setDirty(false);
    };

    useEffect(() => {
        const form = anchor.current?.closest("form");
        if (!form) return undefined;
        const mark = (e) => { if (!e.target?.closest?.("[data-no-dirty]")) setDirty(true); };
        const clean = () => startSubmit(form);
        form.addEventListener("input", mark);
        form.addEventListener("change", mark);
        form.addEventListener(FORM_DIRTY_EVENT, mark);
        form.addEventListener("submit", clean);
        return () => {
            form.removeEventListener("input", mark);
            form.removeEventListener("change", mark);
            form.removeEventListener(FORM_DIRTY_EVENT, mark);
            form.removeEventListener("submit", clean);
        };
    }, []);

    // Plain-button Save (onSave) has no submit event: FormActions bumps submitTick.
    useEffect(() => {
        if (!submitTick) return;
        const form = anchor.current?.closest("form");
        if (form) startSubmit(form);
        else setDirty(false);
    }, [submitTick]); // eslint-disable-line react-hooks/exhaustive-deps

    // Saving finished but we're still here: re-arm if nothing changed.
    const wasSaving = useRef(false);
    useEffect(() => {
        if (disabled) { wasSaving.current = true; return; }
        if (!wasSaving.current) return;
        wasSaving.current = false;
        const form = anchor.current?.closest("form");
        const p = pending.current;
        pending.current = null;
        if (form && p?.wasDirty && formSnapshot(form) === p.snapshot) setDirty(true);
    }, [disabled]);

    const active = dirty && !disabled && !isAutoSaveActive();

    useEffect(() => {
        if (!active) return undefined;
        const warn = (e) => { e.preventDefault(); e.returnValue = ""; };
        window.addEventListener("beforeunload", warn);
        return () => window.removeEventListener("beforeunload", warn);
    }, [active]);

    return (
        <>
            <span ref={anchor} hidden />
            {inRouter && <Prompt when={active} message={UNSAVED_MESSAGE} />}
        </>
    );
}

/**
 * Save-row wrapper that stays reachable on phones: under 767.98px it sticks to
 * the bottom of the screen (safe-area padded) while the form is longer than the
 * screen, and settles back in place once you scroll down to it. Desktop is a
 * plain row. Pages with their own Save row wrap it in this instead of a div.
 *
 * An IntersectionObserver (as in SalesOrderQrScan) flags the stuck state so the
 * bar only gets its top edge/shadow while it is floating over content.
 */
// Toasts on phones sit above a Save bar only while one occupies the bottom of
// the screen. Each StickyActions reports its height while it is there; <html>
// gets data-sticky-save + --sticky-save-h, which App.css uses to lift sonner's
// mobile offset. No bar at the bottom → toasts keep their normal 16px offset.
const bottomBars = new Map(); // element → height (px)
const BOTTOM_BAND = 140;      // a bar whose bottom is this close to the screen edge counts
function publishBottomBars() {
    const root = document.documentElement;
    if (bottomBars.size === 0) {
        delete root.dataset.stickySave;
        root.style.removeProperty("--sticky-save-h");
        return;
    }
    root.dataset.stickySave = "";
    root.style.setProperty("--sticky-save-h", `${Math.max(...bottomBars.values())}px`);
}

function useBottomBarReport(ref) {
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof window === "undefined") return undefined;
        let frame = 0;
        const measure = () => {
            frame = 0;
            const r = el.getBoundingClientRect();
            const vh = window.innerHeight;
            const atBottom = r.height > 0 && r.top < vh && r.bottom > vh - BOTTOM_BAND;
            const had = bottomBars.has(el);
            if (atBottom) bottomBars.set(el, Math.round(Math.min(r.height, vh - r.top)));
            else bottomBars.delete(el);
            if (atBottom || had) publishBottomBars();
        };
        const schedule = () => { if (!frame) frame = requestAnimationFrame(measure); };
        measure();
        window.addEventListener("scroll", schedule, { passive: true });
        window.addEventListener("resize", schedule);
        const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(schedule) : null;
        ro?.observe(el);
        return () => {
            if (frame) cancelAnimationFrame(frame);
            window.removeEventListener("scroll", schedule);
            window.removeEventListener("resize", schedule);
            ro?.disconnect();
            bottomBars.delete(el);
            publishBottomBars();
        };
    }, [ref]);
}

export function StickyActions({ className = "", children, ...rest }) {
    const ref = useRef(null);
    const [stuck, setStuck] = useState(false);
    useBottomBarReport(ref);
    useEffect(() => {
        const el = ref.current;
        if (!el || typeof IntersectionObserver === "undefined") return undefined;
        // CSS puts the bar at bottom:-1px, so while stuck 1px of it is always
        // outside the viewport → ratio < 1. In normal flow it is fully visible.
        const io = new IntersectionObserver(
            ([e]) => setStuck(e.intersectionRatio < 1 && e.boundingClientRect.top < (e.rootBounds?.bottom ?? window.innerHeight)),
            { threshold: [1] },
        );
        io.observe(el);
        return () => io.disconnect();
    }, []);
    return (
        <div ref={ref} className={`sticky-actions${stuck ? " is-stuck" : ""} ${className}`} {...rest}>
            {children}
        </div>
    );
}

/**
 * "Still needed: …" line beside a Save button, so a Save that can't go yet
 * says why (fed by `useFieldErrors().missing`). Renders nothing when empty.
 */
export function NeedsHint({ items = [], style }) {
    if (!items.length) return null;
    return (
        <span className="needs-hint" style={style}>
            Still needed: {items.join(" · ")}
        </span>
    );
}

/**
 * Save + Cancel action row for forms.
 *
 * Props:
 *   onCancel     – () => void
 *   isLoading    – bool         — disables buttons and changes label
 *   saveLabel    – string       (default "Save")
 *   loadingLabel – string       (default "Saving…")
 *   disabled     – bool
 *   needs        – string[]     — what's still missing (shown as a hint)
 *   onSave       – () => void   — makes Save a plain button (type="button")
 *                                 instead of submitting the form
 *   guardUnsaved – bool (default true) — warn before leaving a typed-in,
 *                                 unsaved form (see UnsavedGuard)
 *   submitting / submitLabel – legacy aliases of isLoading / saveLabel
 *                                 (15 pages passed them and silently got an
 *                                 always-enabled Save → double submits)
 */
function FormActions({
    onCancel,
    isLoading: isLoadingProp = false,
    saveLabel: saveLabelProp = "Save",
    submitting,
    submitLabel,
    loadingLabel = "Saving…",
    disabled = false,
    needs,
    onSave,
    guardUnsaved = true,
    style,
    className = "",
}) {
    const isLoading = isLoadingProp || !!submitting;
    const saveLabel = submitLabel ?? saveLabelProp;
    // Clicking a plain-button Save counts as a submit for the guard.
    const [saveClicked, setSaveClicked] = useState(0);
    return (
        <StickyActions className={`form-actions d-flex flex-wrap align-items-center gap-2 pt-1 ${className}`} style={style}>
            {guardUnsaved && <UnsavedGuard submitTick={saveClicked} disabled={isLoading} />}
            <Button
                type={onSave ? "button" : "submit"}
                onClick={onSave ? (e) => { setSaveClicked((n) => n + 1); onSave(e); } : undefined}
                variant="primary"
                size="md"
                disabled={disabled}
                loading={isLoading}
                loadingText={loadingLabel}
                data-enter-save
            >
                {saveLabel}
            </Button>

            {onCancel && (
                <Button
                    variant="outline"
                    size="md"
                    onClick={onCancel}
                    disabled={isLoading || disabled}
                    data-skip-enter
                >
                    Cancel
                </Button>
            )}
            {needs && <NeedsHint items={needs} />}
        </StickyActions>
    );
}

export default FormActions;
